<script setup lang="ts">
import { ref } from 'vue';

import { useColorScheme } from '../composables/useColorScheme';
import { reviewNavItems } from '../nav';

const { scheme, toggle } = useColorScheme();
const CORNER = 48;

// Two drawers, mirroring mui-appshell's own rail (fixed, icon-only, never collapses) + nav panel
// (this app's own pages, collapses away entirely) rather than one drawer that shrinks to icons:
// the rail always shows the one static module mark, since there's only ever one module here;
// toggling hides the panel outright rather than shrinking its items to icons.
const navOpen = ref(true);
</script>

<template>
  <v-app-bar flat border :height="CORNER" class="app-frame__bar">
    <RouterLink :to="{ name: 'home' }" aria-label="Access Review, go to home" class="app-frame__home-link">
      <div
        class="app-frame__corner app-frame__corner--bordered"
        :style="{ inlineSize: `${CORNER}px`, blockSize: `${CORNER}px` }"
      >
        <span class="app-frame__brand-mark" aria-hidden="true" />
      </div>
      <span class="app-frame__brand">Access Review</span>
    </RouterLink>
    <v-spacer />
    <v-icon-btn
      :icon="scheme === 'dark' ? 'light_mode' : 'dark_mode'"
      size="default"
      aria-label="Toggle color scheme"
      @click="toggle"
    />
  </v-app-bar>

  <!-- Module rail: fixed, icon-only, always visible. One module here, so one static mark rather
       than a list to switch between. -->
  <v-navigation-drawer rail :rail-width="CORNER" permanent border>
    <template #prepend>
      <div class="app-frame__corner" :style="{ inlineSize: `${CORNER}px`, blockSize: `${CORNER}px` }">
        <v-icon-btn
          :icon="navOpen ? 'chevron_left' : 'chevron_right'"
          size="small"
          :aria-label="navOpen ? 'Collapse navigation' : 'Expand navigation'"
          @click="navOpen = !navOpen"
        />
      </div>
    </template>
    <div class="app-frame__corner" :style="{ inlineSize: `${CORNER}px`, blockSize: `${CORNER}px` }">
      <!-- Always the current (only) module, so it's drawn pre-highlighted rather than plain, same
           treatment ally gives a permanently-selected rail item. -->
      <div class="app-frame__module-mark">
        <v-icon icon="fact_check" size="small" />
      </div>
    </div>
  </v-navigation-drawer>

  <!-- Nav panel: this app's own pages. Collapses away entirely, leaving only the rail. -->
  <v-navigation-drawer v-model="navOpen" width="240" permanent border>
    <v-list nav>
      <v-list-item :to="{ name: 'home' }" prepend-icon="home" title="Home" />
      <v-list-item
        v-for="item in reviewNavItems"
        :key="item.reviewType"
        :to="{ name: 'reviews', params: { reviewType: item.reviewType } }"
        :prepend-icon="item.icon"
        :title="item.label"
      />
    </v-list>
  </v-navigation-drawer>

  <!-- scrollable: v-main is flex-shrink 0 by default, so it grows with its content and the whole
       page scrolls; this bounds it to the viewport so views can scroll their own regions. -->
  <v-main scrollable>
    <div class="app-frame__container">
      <v-sheet border rounded="lg" class="app-frame__content">
        <slot />
      </v-sheet>
    </div>
  </v-main>
</template>

<style scoped>
.app-frame__bar {
  background-color: var(--surface-ground);
}

.app-frame__corner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.app-frame__corner--bordered {
  border-inline-end: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* The INFORM diamond: a solid square skewed into a rhomboid, colored from the surrounding text
   color rather than the accent so it reads as a mark, not a themed icon. */
.app-frame__brand-mark {
  display: block;
  background-color: currentColor;
  aspect-ratio: 0.875 / 1;
  block-size: 16px;
  transform: skew(-15.5deg);
}

.app-frame__home-link {
  display: flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
}

.app-frame__brand {
  display: flex;
  align-items: center;
  padding-inline-start: 0.5rem;
  font-weight: 600;
}

/* Matches Vuetify's own selected-item treatment (tinted rounded surface, accent-colored icon),
   applied by hand since this mark isn't a real v-list-item. */
.app-frame__module-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 32px;
  block-size: 32px;
  border-radius: 8px;
  background-color: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}

.app-frame__container {
  display: flex;
  block-size: 100%;
  padding: 1rem;
}

.app-frame__content {
  flex: 1 1 auto;
  min-inline-size: 0;
  overflow: auto;
  padding: 1.5rem;
}
</style>
